import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Text, Input, Select, Button, Spinner, Alert, AlertIcon, Icon, Link, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
} from '@chakra-ui/react';
import { FiCheckCircle, FiXCircle, FiExternalLink } from 'react-icons/fi';
import {
  getConstancy, previewConstancy, saveConstancy, constancyDraftPdf, issueConstancy, assignStudentSchool, blobErrorMessage, errorMessage,
  FIELD_SOURCE, formatDateTime,
} from 'api/administrador/documentRequests';
import { StatusPill } from 'components/monitoring/ai/common';

/**
 * Constancia automática de una solicitud (Ventanilla Klazia, fase 3): reglas, institución, datos de la
 * plantilla, borrador en PDF y emisión. Lo calcula la API; aquí solo se completan los datos que falten.
 */

function CheckRow({ c }) {
  return (
    <Flex gap={2.5} align="flex-start" py={1.5}>
      <Icon as={c.ok ? FiCheckCircle : FiXCircle} color={c.ok ? 'green.500' : 'red.500'} mt="3px" flexShrink={0} />
      <Box minW={0}>
        <Text fontSize="13.5px" fontWeight="500">{c.label}</Text>
        {c.detail && <Text fontSize="12.5px" color={c.ok ? 'gray.500' : 'red.600'}>{c.detail}</Text>}
      </Box>
    </Flex>
  );
}

const inputsFrom = (data) => ({
  fields: Object.fromEntries((data?.fields || []).filter((f) => f.source === 'manual').map((f) => [f.key, f.value])),
});

export default function ConstancyPanel({ request, canEdit, onIssued }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [inputs, setInputs] = useState({ fields: {} });
  const [school, setSchool] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await getConstancy(request.id);
      setData(d);
      setInputs(inputsFrom(d));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [request.id]);

  // Se recarga cuando la solicitud cambia (por ejemplo, el tipo de constancia o el estudiante ligado).
  useEffect(() => { load(); }, [load, request.updatedAt]);

  const body = () => ({ fields: inputs.fields });
  const setField = (key, value) => setInputs((i) => ({ ...i, fields: { ...i.fields, [key]: value } }));

  const run = async (name, fn) => {
    setBusy(name);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(name === 'pdf' ? await blobErrorMessage(e) : errorMessage(e));
    } finally {
      setBusy('');
    }
  };

  const refresh = () => run('refresh', async () => {
    const d = await previewConstancy(request.id, body());
    setData(d);
  });

  const save = () => run('save', async () => {
    const d = await saveConstancy(request.id, body());
    setData(d);
    setInputs(inputsFrom(d));
    toast({ status: 'success', title: 'Datos de la constancia guardados', duration: 3000, isClosable: true });
  });

  const saveSchool = () => run('school', async () => {
    const d = await assignStudentSchool(request.id, school);
    setData(d);
    setSchool('');
    toast({ status: 'success', title: 'Colegio guardado en la ficha del estudiante', duration: 3000, isClosable: true });
  });

  const draft = () => run('pdf', async () => {
    const blob = await constancyDraftPdf(request.id, body());
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  });

  const issue = (confirmRules = false) => run('issue', async () => {
    const res = await issueConstancy(request.id, { ...body(), confirmRules });
    setConfirm(null);
    toast({
      status: 'success',
      title: `Constancia emitida · ${res.code}`,
      description: res.emailed ? 'Se envió al correo de la solicitud.' : 'No se envió correo: revisa el correo de la solicitud.',
      duration: 5000,
      isClosable: true,
    });
    await load();
    if (onIssued) onIssued();
  });

  const askIssue = () => {
    const failed = (data?.checks || []).filter((c) => !c.ok && !['estudiante', 'tipo', 'institucion', 'datos'].includes(c.key));
    if (failed.length) setConfirm(failed);
    else issue(false);
  };

  if (loading && !data) return <Flex justify="center" py={6}><Spinner color="#574feb" /></Flex>;

  const issued = data?.issued || request.issuedConstancy;
  if (issued) {
    return (
      <Box border="1px solid" borderColor="green.200" bg="green.50" borderRadius="12px" p={4}>
        <Flex align="center" gap={2} mb={1}>
          <Icon as={FiCheckCircle} color="green.600" />
          <Text fontWeight="600">Constancia emitida</Text>
        </Flex>
        <Text fontSize="sm" color="gray.700">
          Código de verificación <Text as="span" fontFamily="mono" fontWeight="600">{issued.code}</Text>
          {request.issuedConstancy?.issuedAt ? ` · ${formatDateTime(request.issuedConstancy.issuedAt)}` : ''}
          {request.issuedConstancy?.issuedBy ? ` · ${request.issuedConstancy.issuedBy}` : ''}
        </Text>
        {issued.url && (
          <Link href={issued.url} isExternal color="#574feb" fontSize="sm" display="inline-flex" alignItems="center" gap={1} mt={2}>
            Ver PDF <Icon as={FiExternalLink} />
          </Link>
        )}
      </Box>
    );
  }

  if (!data?.template) {
    return (
      <Text fontSize="sm" color="gray.600">
        Para emitirla automáticamente, elige el tipo de constancia (principal, técnicas o pensión), liga la solicitud a un
        estudiante y guarda los cambios.
      </Text>
    );
  }

  const editable = canEdit;
  const failed = (data.checks || []).filter((c) => !c.ok);

  return (
    <Flex direction="column" gap={4}>
      {error && (
        <Alert status="error" borderRadius="8px" fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box>
        <Flex align="center" gap={2} mb={1} wrap="wrap">
          <Text fontWeight="600">{data.template.label}</Text>
          {data.eligible
            ? <StatusPill label="Lista para emitir" tone="green" />
            : <StatusPill label={`${failed.length} ${failed.length === 1 ? 'pendiente' : 'pendientes'}`} tone="orange" />}
        </Flex>
        {data.enrollment && (
          <Text fontSize="12.5px" color="gray.500">
            {data.enrollment.programType} · {data.enrollment.program}{data.enrollment.semester ? ` · ${data.enrollment.semester}` : ''}
          </Text>
        )}
        <Box mt={2}>{(data.checks || []).map((c) => <CheckRow key={c.key} c={c} />)}</Box>
      </Box>

      {data.template.automatable === false && (
        <Alert status="info" borderRadius="8px" fontSize="sm">
          <AlertIcon />
          Esta constancia solo la emite el área: necesita la foto del diploma (fecha de grado) y el valor de la mensualidad.
          El agente la deriva.
        </Alert>
      )}

      <Box>
        <Text fontSize="12px" fontWeight="600" letterSpacing=".06em" textTransform="uppercase" color="gray.500" mb={2}>Institución que certifica</Text>
        {data.institutionSource === 'carrera' ? (
          <>
            <Text fontSize="sm">
              {data.institution ? <>Certifica <b>{data.institution.name}</b></> : 'Sin institución que certifique'}
            </Text>
            <Text fontSize="12px" color="gray.500" mt={1}>
              En técnicas sale de la carrera: las que tienen resolución del Instituto Técnico Iberoamericano. No depende del
              colegio del estudiante.
            </Text>
          </>
        ) : (
          <>
            <Text fontSize="sm">
              Colegio del estudiante: <b>{data.school?.label || 'sin asignar'}</b>
              {data.institution ? <> · certifica <b>{data.institution.name}</b></> : null}
            </Text>
            <Text fontSize="12px" color="gray.500" mt={1}>
              Sale del colegio asignado en la ficha del estudiante, como pide el manual de documentos.
            </Text>
          </>
        )}
        {editable && !data.institution && data.student && data.institutionSource !== 'carrera' && (
          <Flex gap={2} mt={2} wrap="wrap" align="center">
            <Select
              aria-label="Colegio del estudiante" size="sm" borderRadius="8px" maxW="320px"
              value={school} onChange={(e) => setSchool(e.target.value)}
            >
              <option value="">Elegir colegio…</option>
              {(data.schools || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Button size="sm" variant="outline" onClick={saveSchool} isDisabled={!school} isLoading={busy === 'school'}>
              Guardar en la ficha del estudiante
            </Button>
          </Flex>
        )}
      </Box>

      <Box>
        <Text fontSize="12px" fontWeight="600" letterSpacing=".06em" textTransform="uppercase" color="gray.500" mb={2}>Datos de la constancia</Text>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={3}>
          {data.fields.map((f) => {
            const id = `cp-${request.id}-${f.key}`;
            const source = FIELD_SOURCE[f.source];
            const manual = inputs.fields[f.key];
            return (
              <Box key={f.key} minW={0}>
                <Flex justify="space-between" align="center" gap={2} mb={1}>
                  <Text as="label" htmlFor={id} fontSize="13px" color="gray.600">{f.label}</Text>
                  {f.value
                    ? source && <StatusPill label={source.label} tone={source.tone} size="10px" />
                    : <StatusPill label="Falta" tone="red" size="10px" />}
                </Flex>
                <Input
                  id={id} size="sm" borderRadius="8px"
                  isReadOnly={f.auto || !editable}
                  value={manual !== undefined ? manual : f.value}
                  placeholder={f.value ? '' : 'Escribe el dato'}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              </Box>
            );
          })}
        </Grid>
        <Text fontSize="12px" color="gray.500" mt={2}>
          Lo que escribas aquí reemplaza el dato de la plataforma solo en esta constancia.
        </Text>
      </Box>

      {editable && (
        <Flex gap={2} wrap="wrap" justify="flex-end">
          <Button size="sm" variant="ghost" onClick={refresh} isLoading={busy === 'refresh'}>Actualizar revisión</Button>
          <Button size="sm" variant="outline" onClick={save} isLoading={busy === 'save'}>Guardar datos</Button>
          <Button size="sm" variant="outline" onClick={draft} isLoading={busy === 'pdf'}>Ver borrador PDF</Button>
          <Button size="sm" bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={askIssue} isLoading={busy === 'issue'}>
            Emitir constancia
          </Button>
        </Flex>
      )}

      <Modal isOpen={!!confirm} onClose={() => setConfirm(null)} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader fontSize="18px">¿Emitir de todas formas?</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" mb={2}>La solicitud no cumple:</Text>
            {(confirm || []).map((c) => <CheckRow key={c.key} c={c} />)}
            <Text fontSize="sm" color="gray.600" mt={3}>Si la emites, quedará en el historial que se emitió sin cumplir estas reglas.</Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setConfirm(null)}>Cancelar</Button>
            <Button colorScheme="orange" onClick={() => issue(true)} isLoading={busy === 'issue'}>Emitir de todas formas</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
